import utils from 'helpers/utils';
import dom from 'helpers/dom';
import walk from 'helpers/walk';
import constants from 'helpers/constants';
import logger from 'helpers/logger';

function assistantSettled (source) {
  return {source: source, settled: 'assistantState', ready: 'list'};
}

function assistantInstruction (targets, value, done) {
  return [
    {
      action: 'type',
      target: targets.assistantForm,
      value: value,
      narration: 'We type the instruction for the assistant.',
      fallback: [{action: 'click', target: targets.assistantActions, label: /^Add( instruction)?$/, narration: 'We open the instruction box.'}]
    },
    {action: 'click', target: targets.assistantSubmit, narration: 'We send the instruction to the assistant.'},
    done
  ];
}

function applyProposal (targets, source, actions) {
  return [
    {action: 'mark', source: source, size: 'assistantState.assistantActions', key: 'applied'},
    {action: 'click', target: actions, within: targets.collectionConversation, label: 'Apply', narration: 'We apply what the assistant proposes.'},
    {action: 'click', target: targets.confirmDialog, label: 'Confirm', narration: 'We confirm the change.'},
    {action: 'click', target: targets.confirmDialog, label: 'No', optional: true, narration: 'We leave the auto tags service off, the tour tags the companies itself.'},
    {
      action: 'until',
      narration: 'The change is being saved to your collection.',
      check: {source: source, size: 'assistantState.assistantActions', grown: 'applied', quiet: targets.collectionConversation},
      timeout: constants.tour.timing.short
    }
  ];
}

function definition (value) {
  switch (value) {
    case constants.tour.types.introduction: {
      const adviceTypes = constants.assistant.adviceActionTypes;
      const questionCard = `.ConversationCard-actions:has(.ActionCard-type-${adviceTypes.askQuestion}, .ActionCard-type-${adviceTypes.needMoreInfo})`;
      const proposalCard = `.ConversationCard-actions:not(${questionCard})`;
      const columns = constants.collection.entity.columnDefinition;
      const scoreHeader = `[class~="TableHeader-${columns.collectionAnalysisScoreGraph.sortingKey}"]`;
      const relevanceHeaders = [columns.externalRelevancy, columns.personalRelevancy].map((column) => {
        return `[class~="TableHeader-${column.sortingKey}"]`;
      }).join(', ');

      const targets = {
        menu: '.MenuDrawer-buttons-top',
        queryBar: '.QueryBar',
        confirmDialog: '.ConfirmDialog',
        assistant: '.AssistantQuery',
        assistantForm: '.AssistantQuery-form textarea',
        assistantActions: '.AssistantQuery-actions-right',
        assistantSubmit: '.AssistantQuery-actions-right button[type="submit"]',
        databaseCount: '.DatabaseEntitiesTableBar .PageBar-title',
        databaseTable: '.DatabaseEntitiesTable',
        actionbar: '.EntitiesActionbar',
        selectionDialog: '.CollectionSelectionDialog',
        addDialog: '.CollectionAddDialog',
        addDialogSubmit: '.CollectionAddDialog button[type="submit"]',
        collectionTable: '.CollectionEntitiesTable',
        collectionHeader: '.CollectionEntitiesTable .TableHeader',
        collectionScore: `.CollectionEntitiesTable ${scoreHeader}`,
        collectionScoreSort: `.CollectionEntitiesTable ${scoreHeader} .Mui-TableHeadCell-Content-Labels`,
        collectionScoreDesc: `.CollectionEntitiesTable ${scoreHeader} .MuiTableSortLabel-root.Mui-active.MuiTableSortLabel-directionDesc`,
        recentCollection: '.RecentCollectionsDashboardCard .CollectionCard:first-child',
        recentFavorite: '.RecentCollectionsDashboardCard .CollectionCard:first-child .CollectionCard-dealflow-favorite',
        recentFavorited: '.RecentCollectionsDashboardCard .CollectionCard:first-child .CollectionCard-dealflow-favorite.Mui-checked',
        addDialogPrivacy: '.CollectionAddDialog input',
        privacyOption: '[role="listbox"] [role="option"]',
        conversation: '.AssistantQuery-conversation',
        collectionBarActions: '.CollectionEntitiesTableBar .PageBar-rightActions',
        collectionConversation: '.Assistant-conversation',
        collectionNavigation: '.Assistant-conversation-navigation',
        collectionActions: '.Assistant-conversation-actions-right',
        collectionClose: '.Assistant-toolbar-close',
        collectionForm: '.Assistant-conversation-form textarea',
        collectionSubmit: '.Assistant-conversation-actions-right button[type="submit"]',
        question: questionCard,
        questionActions: `${questionCard} .ConversationCard-actions-footer-right`,
        proposalActions: `${proposalCard} .ConversationCard-actions-footer-right`,
        onboarding: '.CollectionOnboarding',
        onboardingProgress: '.CollectionOnboarding-progress',
        profileClose: '.ProfileFoldout-toolbar-close',
        relevancyCard: '.CollectionEntitySectionPanel .RelevancyContextCard',
        relevancyButton: '.CollectionEntitySectionPanel .RelevancyContextCard-button',
        relevancyFoldout: '.ContextPopper-paper:has(.DropdownPopper)',
        calibrationOption: `${questionCard} input[type="radio"]`,
        relevanceHeader: `.CollectionEntitiesTable ${relevanceHeaders}`
      };

      const sources = {
        database: 'table:database.entity',
        collectionEntities: 'table:collection.entity',
        collection: 'profile:collection'
      };

      const found = {...assistantSettled(sources.database), amount: 'list.meta.resultsCount'};

      const limits = {
        minimum: 10,
        shortList: 300,
        research: 100,
        collection: 25
      };

      const processing = 5 * 60 * 1000;
      const collectionName = `{collectionName} (${value})`;
      const processLabel = /^(Process|Retry)/;
      const processMore = {...found, below: limits.collection, exists: {target: targets.assistantActions, label: processLabel}};
      const adviseLabel = /^Advise( next)?( step)?$/;
      const categories = 2;

      const resetSearch = [
        {action: 'click', target: targets.queryBar, label: 'Reset', optional: true, focus: false},
        {action: 'click', target: targets.confirmDialog, label: 'Confirm', optional: true, focus: false}
      ];

      const searching = [targets.assistant, targets.databaseCount];

      const databaseDone = {
        action: 'until',
        narration: 'The assistant is working on it. A search takes a few seconds, sometimes a little longer, the results appear in the table.',
        check: {
          ...assistantSettled(sources.database),
          quiet: [targets.conversation, targets.databaseTable],
          loaded: targets.databaseCount
        },
        watch: searching,
        timeout: processing
      };

      const collected = {data: 'collected'};

      const openAssistant = [
        {action: 'navigate', to: '/collections/{collectionId}/entities'},
        {action: 'click', target: targets.collectionBarActions, index: 0, narration: 'We open the assistant of your collection.', when: {absent: {target: targets.collectionConversation, self: true}}},
        {action: 'waitFor', target: targets.collectionConversation}
      ];

      const question = {target: targets.question, within: targets.collectionConversation, self: true};
      const asking = {exists: question};
      const categoriesInstruction = `Suggest exactly ${categories} categories to score the collection companies on`;
      const categoriesNarration = `We ask for exactly ${categories} categories, so the scoring stays easy to follow.`;
      const categoriesRetry = `Suggest exactly ${categories} categories, a technology breakdown and a market position breakdown`;
      const proposal = {target: targets.proposalActions, within: targets.collectionConversation, label: 'Apply'};
      const proposing = {exists: proposal};
      const askingAgain = {exists: question, absent: proposal};
      const categoriesAdded = {source: sources.collection, size: 'data.tagGroups', atLeast: categories};
      const tagging = {target: targets.onboardingProgress, self: true};

      const tableReady = {action: 'until', check: {source: sources.collectionEntities, ready: 'list', rows: targets.collectionTable}, watch: targets.collectionTable};
      const refreshScore = {action: 'click', target: targets.collectionScoreSort, self: true, narration: 'We sort on the collection score again, so the new points show.'};
      const sortScore = [
        tableReady,
        {action: 'click', target: targets.collectionScoreSort, self: true, narration: 'We sort on the collection score, so the best matches come first.', when: {absent: {target: targets.collectionScoreDesc, self: true}}},
        tableReady,
        {action: 'click', target: targets.collectionScoreSort, self: true, narration: 'Once more, so the highest score comes first.', when: {absent: {target: targets.collectionScoreDesc, self: true}}},
        {
          action: 'until',
          check: {
            source: sources.collectionEntities,
            ready: 'list',
            exists: {target: targets.collectionScoreDesc, self: true}
          },
          watch: targets.collectionTable
        }
      ];

      const ratingRows = [0, -1, 'middle', 1, -2];
      const relevancies = constants.data.hml.filter((r) => {
        return r.value > 0;
      });
      const unrated = {
        any: relevancies.map((r) => {
          return {missing: `relevancy${r.value}`};
        })
      };
      const recordRating = relevancies.map((r) => {
        return {action: 'set', key: `relevancy${r.value}`, value: true, when: {exists: {target: `${targets.relevancyButton}.Button-color-${r.buttonColor}`, self: true}}};
      });
      const noRating = `${targets.relevancyButton}.Button-color-${constants.data.lookup('hml', 0).buttonColor}`;
      const calibrationOption = {target: targets.calibrationOption, within: targets.collectionConversation, self: true, index: '{calibration.calibrate}'};
      const calibrationOffered = {data: 'calibration.calibrate'};
      const deciderOffered = {data: 'calibration.decider'};

      const replied = [
        {
          action: 'until',
          narration: 'The assistant is preparing its answer, this can take a minute.',
          check: {...assistantSettled(sources.collectionEntities), quiet: targets.collectionConversation},
          watch: targets.collectionConversation,
          timeout: processing
        }
      ];

      return {
        scenes: [
          {
            name: 'search',
            title: 'Search the database',
            aside: `The assistant turns a plain sentence into a search across the millions of companies in ${constants.basics.shortName}.`
          },
          {
            name: 'research',
            title: 'Research with AI',
            aside: 'Research with AI reads every company and keeps only the ones that match what you are looking for.'
          },
          {
            name: 'create',
            title: 'Create your collection',
            aside: 'A collection is your own list of companies. Private collections are yours alone, team collections are shared with your team.'
          },
          {
            name: 'assistant',
            title: 'Sharpen with the assistant',
            aside: 'The collection assistant advises the next step and applies it for you.'
          },
          {
            name: 'score',
            title: 'Score your companies',
            aside: 'Categories tag every company, and the points of its tags add up to its score.'
          },
          {
            name: 'rate',
            title: 'Rate your companies',
            aside: 'Your relevance ratings, Low, Medium or High, tell the scoring what a good match looks like for you.'
          },
          {
            name: 'calibrate',
            title: 'Calibrate the scoring',
            aside: 'The assistant compares the scores with your ratings and balances the category points so they agree.'
          }
        ],
        dialogs: {
          welcome: {
            title: `Welcome to ${constants.basics.shortName}`,
            description: `Tell us which companies you are looking for. ${constants.basics.shortName} will search the database, research the best matches with AI and build your first collection while you watch.`,
            fields: [
              {
                name: 'companies',
                entity: 'tour',
                label: 'Which companies are you looking for?',
                placeholder: 'Describe the companies to search for in one sentence',
                type: constants.formFieldTypes.textarea,
                validation: constants.formFieldValidationTypes.text,
                required: true,
                FormFieldProps: {
                  minRows: 2
                }
              },
              {
                name: 'country',
                entity: 'tour',
                label: 'In which country?',
                placeholder: 'Search for a country',
                type: constants.formFieldTypes.autocomplete,
                conversion: constants.formFieldConversionTypes.label,
                options: 'countries',
                required: true
              }
            ]
          },
          finish: {
            title: 'That concludes the introduction',
            description: [
              'Here is what we did together:',
              '- You described the companies you are looking for',
              '- The assistant searched the database and researched the best matches with AI',
              '- We added the best matches to a new private collection',
              '- We made it a favorite, so it is monitored for new insights on your dashboard',
              '- We opened it from your dashboard and scored its companies on two categories',
              '- You rated a few companies and the assistant calibrated the scoring on your ratings',
              ...(constants.basics.links?.guide ?
                ['', `Want to learn more? Read the [${constants.basics.shortName} guide](${constants.basics.links.guide}).`] : [])
            ].join('\n')
          }
        },
        steps: [
          {
            name: 'database.search',
            scene: 'search',
            watch: searching,
            unless: collected,
            narration: 'We start in the database, where the assistant turns your description into a search.',
            reason: 'The assistant could not run the search for your description, so we can not move on. Try again with a different description.',
            actions: [
              {action: 'navigate', to: '/database', via: {target: targets.menu, index: 1}},
              ...resetSearch,
              {action: 'click', target: targets.queryBar, label: 'Assistant', optional: true, narration: 'We open the search assistant.'},
              ...assistantInstruction(targets, 'Find {companies} in {country}', databaseDone)
            ]
          },
          {
            name: 'database.widen',
            scene: 'search',
            unless: collected,
            resume: 'database.search',
            narration: 'Too few companies found yet, so we ask the assistant to broaden the search.',
            reason: 'The assistant could not broaden the search, so we can not move on.',
            when: {...found, below: limits.minimum},
            actions: assistantInstruction(targets, 'Find more companies by broadening the search', databaseDone)
          },
          {
            name: 'database.widen.countries',
            scene: 'search',
            unless: collected,
            resume: 'database.search',
            narration: 'Still too few companies in your country, so we include other countries as well.',
            reason: 'The assistant could not include other countries in the search, so we can not move on.',
            when: {...found, below: limits.minimum},
            actions: assistantInstruction(targets, 'Broaden the search more to include companies in other countries as well', databaseDone)
          },
          {
            name: 'database.empty',
            scene: 'search',
            unless: collected,
            resume: 'database.search',
            narration: 'The search did not find enough companies.',
            when: {...found, below: limits.minimum},
            actions: [
              {action: 'stop', reason: `The database search did not find enough companies, so we can not move on. We need at least ${limits.minimum} companies to continue. Try again with a broader description.`}
            ]
          },
          {
            name: 'database.narrow',
            scene: 'search',
            unless: collected,
            resume: 'database.search',
            narration: 'That is a long list, so we ask the assistant to narrow it down to a shortlist.',
            reason: 'The assistant could not narrow the search to a shortlist, so we can not move on.',
            when: {...found, above: limits.shortList},
            actions: assistantInstruction(targets, 'Find the best matching companies and narrow the search to a shortlist', databaseDone)
          },
          {
            name: 'database.research',
            scene: 'research',
            unless: collected,
            resume: 'database.search',
            narration: 'We found {count} companies. Now the assistant researches them with AI, 100 at a time, to pick the best ones.',
            narrationValues: {count: found},
            reason: 'Researching the companies with AI did not finish, so we can not move on.',
            when: {...found, atLeast: limits.research},
            actions: assistantInstruction(targets, 'Research these companies and keep the ones that best match {companies}', databaseDone)
          },
          {
            name: 'database.process',
            scene: 'research',
            unless: collected,
            resume: 'database.search',
            narration: 'Only {count} good matches so far, so the assistant researches the next 100 companies with AI.',
            narrationValues: {count: found},
            reason: 'Researching the next companies with AI did not finish, so we can not move on.',
            when: processMore,
            repeat: {
              while: processMore,
              max: 2
            },
            actions: [
              {action: 'click', target: targets.assistantActions, label: processLabel, narration: 'We let the assistant research the next companies.'},
              databaseDone
            ]
          },
          {
            name: 'database.collect',
            scene: 'create',
            watch: searching,
            unless: collected,
            resume: 'database.search',
            narration: 'We add the best matches to a new private collection. Private collections are yours alone, team collections are shared with your team.',
            reason: 'The companies could not be added to a new collection, so we can not move on.',
            actions: [
              {action: 'until', check: {...assistantSettled(sources.database), rows: targets.databaseTable, quiet: targets.databaseTable, loaded: targets.databaseCount}},
              {action: 'run', task: 'nameCollection', into: 'collectionName', source: sources.database, when: {missing: 'collectionName'}},
              {action: 'until', check: {data: 'collectionName'}, timeout: constants.tour.timing.short},
              {action: 'select', target: targets.databaseTable, count: {...found, max: limits.collection}, narration: 'We select the best matches.'},
              {action: 'click', target: targets.actionbar, label: 'Add to collection', narration: 'We add them to a new private collection.'},
              {action: 'click', target: targets.selectionDialog, label: 'New', narration: 'We create a new collection.'},
              {action: 'type', target: targets.addDialog, value: collectionName, narration: 'We name it after your search.'},
              {action: 'type', target: targets.addDialogPrivacy, index: 1, value: 'Private', narration: 'We make it a private collection, only you can see it.'},
              {action: 'click', target: targets.privacyOption, label: 'Private', self: true, narration: 'We pick Private.'},
              {action: 'click', target: targets.addDialogSubmit, narration: 'We save the new collection.'},
              {action: 'waitFor', target: targets.selectionDialog, gone: true},
              {action: 'set', key: 'collected', value: true}
            ]
          },
          {
            name: 'dashboard.collection',
            scene: 'create',
            unless: {data: 'collectionId'},
            narration: 'Your recent collections are always on your dashboard.',
            reason: 'Your new collection did not show up on your dashboard, so we can not move on.',
            actions: [
              {action: 'navigate', to: '/', via: {target: targets.menu, index: 0}},
              {action: 'until', check: {loaded: targets.recentCollection, contains: targets.recentCollection, value: collectionName}, watch: targets.recentCollection},
              {action: 'reveal', target: targets.recentCollection, self: true, align: 'center', highlight: true},
              {action: 'click', target: targets.recentFavorite, self: true, narration: 'We make it a favorite. Your favorite collections are monitored for new insights, and you see them on your dashboard every time you log in.', when: {missing: 'favorited'}},
              {action: 'set', key: 'favorited', value: true},
              {action: 'until', check: {exists: {target: targets.recentFavorited, self: true}}},
              {action: 'wait'},
              {action: 'click', target: targets.recentCollection, self: true, narration: 'We open the collection we just created.'},
              {action: 'resolve', key: 'collectionId', pattern: '^/collections/(\\d+)'}
            ]
          },
          {
            name: 'collection.open',
            scene: 'create',
            narration: 'This is your first collection. Its companies appear here as they are added.',
            reason: 'Your new collection could not be opened, so we can not move on.',
            actions: [
              {action: 'navigate', to: '/collections/{collectionId}/entities'},
              {action: 'until', check: {rows: targets.collectionTable}, watch: targets.collectionTable}
            ]
          },
          {
            name: 'collection.advise',
            scene: 'assistant',
            resume: 'collection.advise',
            unless: categoriesAdded,
            watch: targets.collectionConversation,
            narration: 'Your collection has its own assistant. We ask it to advise the next step.',
            reason: 'The collection assistant did not give its advice, so we can not move on.',
            actions: [
              ...openAssistant,
              {action: 'click', target: targets.collectionActions, label: adviseLabel, narration: 'We ask the assistant to advise the next step for your collection.', when: {source: sources.collectionEntities, size: 'assistantState.assistantConversation', below: 1}},
              ...replied
            ]
          },
          {
            name: 'collection.answer',
            scene: 'assistant',
            resume: 'collection.advise',
            unless: categoriesAdded,
            watch: targets.collectionConversation,
            when: asking,
            repeat: {
              while: asking,
              max: 2
            },
            narration: 'The assistant has a few questions to sharpen your collection.',
            reason: 'The assistant did not continue after your answers, so we can not move on.',
            actions: [
              {action: 'mark', source: sources.collectionEntities, size: 'assistantState.assistantConversation', key: 'answered'},
              {
                action: 'handover',
                target: targets.question,
                within: targets.collectionConversation,
                self: true,
                narration: 'Answer the questions of the assistant, then click Apply.',
                until: {source: sources.collectionEntities, size: 'assistantState.assistantConversation', grown: 'answered'}
              },
              ...replied
            ]
          },
          {
            name: 'collection.apply',
            scene: 'assistant',
            resume: 'collection.advise',
            unless: categoriesAdded,
            watch: targets.collectionConversation,
            when: proposing,
            repeat: {
              while: proposing,
              max: 3
            },
            narration: 'Based on your answers the assistant proposes a change. We apply it for you.',
            reason: 'The change the assistant proposed could not be applied, so we can not move on.',
            actions: applyProposal(targets, sources.collectionEntities, targets.proposalActions)
          },
          {
            name: 'collection.score',
            scene: 'score',
            resume: 'collection.score',
            unless: categoriesAdded,
            watch: targets.collectionConversation,
            narration: 'Now we let the assistant analyse your companies and score them on categories.',
            reason: 'The assistant did not propose categories to score your companies on, so we can not move on.',
            actions: [
              ...openAssistant,
              ...replied,
              {action: 'click', target: targets.collectionNavigation, label: 'Analyse & score', narration: 'We ask the assistant to analyse your companies and score them.'},
              ...replied,
              {action: 'type', target: targets.collectionForm, value: categoriesInstruction, narration: categoriesNarration, when: {absent: [question, proposal]}},
              {action: 'click', target: targets.collectionSubmit, narration: 'We send it to the assistant.', when: {absent: [question, proposal]}},
              {action: 'type', target: targets.question, within: targets.collectionConversation, value: categoriesInstruction, narration: categoriesNarration, when: asking},
              {action: 'click', target: targets.questionActions, within: targets.collectionConversation, label: 'Apply', narration: 'We send our answer to the assistant.', when: asking},
              ...replied,
              {action: 'type', target: targets.question, within: targets.collectionConversation, value: categoriesRetry, narration: 'The assistant asks again, so we name the two categories we want.', when: askingAgain},
              {action: 'click', target: targets.questionActions, within: targets.collectionConversation, label: 'Apply', narration: 'We send our answer to the assistant.', when: askingAgain},
              ...replied
            ]
          },
          {
            name: 'collection.categories',
            scene: 'score',
            resume: 'collection.score',
            unless: categoriesAdded,
            watch: targets.collectionConversation,
            repeat: {
              while: proposing,
              max: 3
            },
            narration: 'We apply what the assistant proposes for scoring. Every company in your collection will be scored on the categories.',
            reason: 'The categories could not be added to your collection, so we can not move on.',
            actions: applyProposal(targets, sources.collectionEntities, targets.proposalActions)
          },
          {
            name: 'collection.tagging',
            scene: 'score',
            resume: 'collection.tagging',
            unless: {data: 'tagged'},
            narration: 'The assistant now tags every company in your collection with the new categories. You can follow the progress here.',
            reason: 'Tagging your companies with the categories did not finish, so we can not move on.',
            actions: [
              {action: 'navigate', to: '/collections/{collectionId}/entities'},
              {action: 'until', check: categoriesAdded, narration: 'The categories are being added to your collection.', timeout: constants.tour.timing.short},
              {action: 'click', target: targets.collectionClose, focus: false, when: {exists: {target: targets.collectionConversation, self: true}}},
              {action: 'waitFor', target: targets.collectionConversation, gone: true},
              {action: 'until', check: {source: sources.collectionEntities, ready: 'list', rows: targets.collectionTable}, watch: targets.collectionTable},
              {action: 'reveal', target: targets.collectionScore, search: targets.collectionHeader, self: true, align: 'left', highlight: true, narration: 'The collection score adds up the points of the tags, the categories follow it.'},
              {action: 'run', task: 'tagCollection', key: 'tagged', collection: sources.collection, entities: sources.collectionEntities, narration: 'We now tag every company in your collection with the new categories.'},
              {action: 'until', check: {any: [{exists: tagging}, {data: 'tagged'}]}, timeout: processing},
              {action: 'reveal', target: targets.onboardingProgress, self: true, highlight: true, narration: 'You can follow the tagging on the round button, it usually takes a minute or two.', when: {exists: tagging}},
              {
                action: 'until',
                check: {
                  data: 'tagged',
                  absent: tagging
                },
                watch: targets.onboarding,
                timeout: processing
              },
              ...sortScore,
              {action: 'wait'}
            ]
          },
          {
            name: 'collection.rate',
            scene: 'rate',
            resume: 'collection.rate',
            when: unrated,
            repeat: {
              while: unrated,
              max: ratingRows.length
            },
            narration: 'Your ratings teach the scoring what you find relevant. We open a few companies for you to rate.',
            reason: 'The companies could not be opened for rating, so we can not move on.',
            actions: [
              {action: 'navigate', to: '/collections/{collectionId}/entities', when: {absent: {target: targets.collectionTable, self: true}}},
              {action: 'click', target: targets.collectionTable, row: {source: sources.collectionEntities, size: 'list.data', at: ratingRows}, narration: 'We open the next company.'},
              {action: 'until', check: {loaded: targets.relevancyCard}},
              {action: 'click', target: targets.relevancyButton, self: true, when: {absent: {target: targets.relevancyFoldout, self: true}}},
              {
                action: 'handover',
                target: targets.relevancyFoldout,
                self: true,
                narration: 'How relevant is this company to you? Pick Low, Medium or High.',
                until: {absent: {target: noRating, self: true}}
              },
              ...recordRating
            ]
          },
          {
            name: 'collection.calibrate',
            scene: 'calibrate',
            resume: 'collection.calibrate',
            watch: targets.collectionConversation,
            narration: 'Now the assistant compares the scores with your ratings.',
            reason: 'The assistant could not compare the scores with your ratings, so we can not move on.',
            actions: [
              {action: 'click', target: targets.profileClose, focus: false, when: {exists: {target: targets.profileClose}}},
              ...openAssistant,
              ...replied,
              {action: 'click', target: targets.collectionNavigation, label: 'Analyse & score', narration: 'We ask the assistant to analyse the scores against your ratings.'},
              ...replied,
              {action: 'set', key: 'calibration', value: null},
              {action: 'run', task: 'calibration', into: 'calibration', source: sources.collectionEntities},
              {action: 'until', check: {data: 'calibration'}},
              {action: 'wait', narration: 'The assistant also offers to find a decider category, one that separates companies that now score the same. We skip it to keep the introduction short.', when: deciderOffered},
              {action: 'wait', narration: 'The assistant has nothing to calibrate, your scoring stays as it is.', when: {missing: 'calibration.calibrate'}},
              {action: 'click', ...calibrationOption, narration: 'We let the assistant balance the category points with your ratings.', when: calibrationOffered},
              {action: 'click', target: targets.questionActions, within: targets.collectionConversation, label: 'Apply', narration: 'We send the choice to the assistant.', when: calibrationOffered},
              {action: 'set', key: 'calibrating', value: true, when: calibrationOffered},
              ...replied
            ]
          },
          {
            name: 'collection.calibration',
            scene: 'calibrate',
            resume: 'collection.calibrate',
            watch: targets.collectionConversation,
            when: {data: 'calibrating'},
            repeat: {
              while: proposing,
              max: 3
            },
            narration: 'The assistant proposes new points for the categories. We apply them.',
            reason: 'The new points could not be applied, so we can not move on.',
            actions: applyProposal(targets, sources.collectionEntities, targets.proposalActions)
          },
          {
            name: 'collection.result',
            scene: 'calibrate',
            resume: 'collection.result',
            narration: 'Here is the result: your companies sorted on their score, with your ratings next to it.',
            reason: 'The result could not be shown.',
            actions: [
              {action: 'navigate', to: '/collections/{collectionId}/entities'},
              {action: 'click', target: targets.collectionClose, focus: false, when: {exists: {target: targets.collectionConversation, self: true}}},
              {action: 'waitFor', target: targets.collectionConversation, gone: true},
              tableReady,
              {action: 'reveal', target: targets.relevanceHeader, search: targets.collectionHeader, self: true, align: 'left', highlight: true, narration: 'Your ratings sit in this column, compare them with the scores.'},
              refreshScore,
              ...sortScore,
              {action: 'wait'}
            ]
          }
        ]
      };
    }
    default:
      return null;
  }
}

function fill (text, values) {
  return `${text ?? ''}`.replace(/\{([\w.]+)\}/g, (match, key) => {
    const value = valueAt(values, key);

    if (utils.isDefined(value)) {
      return `${value}`.replace(/\s+/g, ' ').trim();
    } else {
      throw failure(`Tour value ${key} is missing`);
    }
  });
}

function narration (text, narrationValues, values) {
  return fill(text, Object.keys(narrationValues ?? {}).reduce((o, key) => {
    const value = valueAt(sourceOf(narrationValues[key], values), narrationValues[key].amount);
    o[key] = utils.isNumber(value) ? utils.formatNumber(value) : value;
    return o;
  }, {...values}));
}

function failure (message, cancelled = false, reason = null) {
  const error = new Error(message);
  error.cancelled = cancelled;
  error.reason = reason;
  return error;
}

async function pause (ms, context) {
  await utils.sleep(ms);
  while (context.paused() && !context.cancelled()) {
    await utils.sleep(context.timing.poll);
  }
  if (context.cancelled()) {
    throw failure('Tour closed', true);
  }
  if (context.failed()) {
    throw context.failed();
  }
}

function activity (watch) {
  return utils.toArray(watch).map((target) => {
    return [...document.querySelectorAll(target)].map((el) => {
      return el.textContent;
    }).join('|');
  }).join('#');
}

async function waitUntil (test, timeout, context, watch = null) {
  let elapsed = 0;
  let seen = watch ? activity(watch) : null;
  let result = test();

  while (!result && elapsed < timeout) {
    await pause(context.timing.poll, context);
    elapsed += context.timing.poll;

    if (watch) {
      const now = activity(watch);
      if (now !== seen) {
        seen = now;
        elapsed = 0;
      }
    }

    result = test();
  }

  return result;
}

function element (action, values) {
  const options = {
    label: action.label,
    index: utils.isString(action.index) ? +fill(action.index, values) : action.index,
    field: action.action === 'type',
    self: action.self
  };

  if (action.within) {
    return walk.lastListItems(action.within, 2).reduce((el, item) => {
      return el ?? walk.findElement([...item.querySelectorAll(action.target)], options);
    }, null);
  } else {
    return walk.findElement(action.target, options);
  }
}

function isLoaded (target) {
  const el = document.querySelector(target);
  return Boolean(el) && !walk.hasSkeleton(el);
}

function sourceOf (check, values) {
  return check.source ? (values?.reports?.[check.source] ?? null) : values;
}

function valueAt (source, path) {
  return path.split('.').reduce((value, key) => {
    return value?.[key];
  }, source);
}

function isReady (list) {
  return utils.isDefined(list?.data) && !list?.status?.isLoading;
}

function isSettled (assistant) {
  const message = utils.toArray(assistant?.assistantConversation).findLast((c) => {
    return c.role === constants.assistant.conversation.roles.user;
  });
  const pending = utils.toArray(message?.results?.actions).filter((action) => {
    return constants.data.lookup('assistantAdviceActions', action.id)?.auto &&
      !utils.toArray(assistant?.assistantActions).find((a) => {
        return a.applied && a.actionId === action.actionId;
      });
  });

  return +(message?.progress?.part ?? 0) === 100 && pending.length === 0;
}

function isQuiet (targets) {
  return utils.toArray(targets).every((target) => {
    return !walk.hasSkeleton(target);
  });
}

function holds (check, values = null, marks = null) {
  const source = check ? sourceOf(check, values) : null;
  const count = check?.amount ? valueAt(source, check.amount) : (
    check?.size ? utils.toArray(valueAt(source, check.size)).length : null
  );

  return Boolean(check) &&
    (!check.settled || isSettled(valueAt(source, check.settled))) &&
    (!check.ready || isReady(check.ready === true ? source : valueAt(source, check.ready))) &&
    (!(check.amount || check.size) || (utils.isDefined(count) &&
      (!utils.isDefined(check.above) || count > check.above) &&
      (!utils.isDefined(check.below) || count < check.below) &&
      (!utils.isDefined(check.atLeast) || count >= check.atLeast) &&
      (!check.grown || count > (marks?.[check.grown]?.size ?? Infinity)))) &&
    (!check.quiet || isQuiet(check.quiet)) &&
    (!check.any || check.any.some((c) => {
      return holds(c, values, marks);
    })) &&
    (!check.loaded || isLoaded(check.loaded)) &&
    (!check.exists || Boolean(element({...check.exists, action: 'click'}, values))) &&
    (!check.absent || utils.toArray(check.absent).every((absent) => {
      return !element({...absent, action: 'click'}, values);
    })) &&
    (!check.rows || walk.findRows(check.rows).length > 0) &&
    (!check.contains || Boolean(document.querySelector(check.contains)?.textContent.includes(fill(check.value, values)))) &&
    (!check.data || utils.isDefined(valueAt(values, check.data))) &&
    (!check.missing || !utils.isDefined(valueAt(values, check.missing)));
}

async function reveal (el, context, align = 'auto') {
  for (let attempt = 0; attempt < context.timing.scrollAttempts; attempt++) {
    dom.scrollIntoView(el, {behavior: 'smooth', align: align});
    await pause(context.timing.scroll, context);
  }
}

async function press (el, context) {
  await reveal(el, context);

  if (walk.isPressable(el)) {
    const bbox = el.getBoundingClientRect();
    const point = {bubbles: true, cancelable: true, button: 0, clientX: bbox.left + (bbox.width / 2), clientY: bbox.top + (bbox.height / 2)};

    el.dispatchEvent(new MouseEvent('mousedown', point));
    await pause(context.timing.press, context);
    el.dispatchEvent(new MouseEvent('mouseup', point));
  }
}

async function typeInto (el, value, context) {
  el.focus({preventScroll: true});

  const steps = Math.min(value.length, context.timing.typeSteps);
  for (let i = 1; i <= steps; i++) {
    walk.setValue(el, value.slice(0, Math.ceil(value.length * i / steps)));
    await pause(context.timing.typing, context);
  }

  if (el.value !== value) {
    walk.setValue(el, value);
    await pause(context.timing.typing, context);
  }

  if (el.value !== value) {
    throw failure(`Typing into ${el.tagName.toLowerCase()} did not take`);
  }
}

async function search (find, step, context) {
  let found = null;

  for (let attempt = 0; !found && attempt < 20; attempt++) {
    found = await waitUntil(find, 3 * context.timing.poll, context);

    if (!found) {
      step();
      await pause(context.timing.scroll, context);
    }
  }

  return found;
}

async function findRow (action, context) {
  const count = utils.toArray(valueAt(sourceOf(action.row, context.values()), action.row.size)).length;
  const at = action.row.at[context.round ?? 0];
  const index = at === 'middle' ? Math.floor(count / 2) : (at < 0 ? count + at : at);

  return search(() => walk.findRow(action.target, index), () => {
    const shown = walk.findRows(action.target).map((row) => {
      return +row.getAttribute('data-index');
    });
    window.scrollBy({top: (index > Math.max(...shown) ? 1 : -1) * Math.floor(window.innerHeight / 2), behavior: 'smooth'});
  }, context);
}

async function selectRows (action, context) {
  const count = Math.min(action.count.max, valueAt(sourceOf(action.count, context.values()), action.count.amount) ?? 0);
  const first = await waitUntil(() => walk.findRowCheckbox(action.target, 0), action.timeout ?? context.timing.timeout, context, action.watch);

  if (first && count > 0) {
    await press(first, context);
    first.click();
    await pause(context.timing.action, context);

    if (count > 1) {
      const last = await search(() => walk.findRowCheckbox(action.target, count - 1), () => {
        window.scrollBy({top: Math.floor(window.innerHeight / 2), behavior: 'smooth'});
      }, context);

      if (last) {
        await press(last, context);
        window.dispatchEvent(new KeyboardEvent('keydown', {key: 'Shift', shiftKey: true}));
        last.click();
        window.dispatchEvent(new KeyboardEvent('keyup', {key: 'Shift', shiftKey: false}));
        await pause(context.timing.action, context);
      } else {
        throw failure(`Row ${count} not found in ${action.target}`);
      }
    }
  } else {
    throw failure(`No rows found in ${action.target}`);
  }
}

async function until (action, context) {
  let since = null;

  const done = await waitUntil(() => {
    const held = holds(action.check, context.values(), context.marks);
    since = held ? (since ?? performance.now()) : null;
    return held && (performance.now() - since) >= context.timing.settle;
  }, action.timeout ?? context.timing.timeout, context, action.watch);

  if (!done) {
    throw failure(`Waiting for ${JSON.stringify(action.check)} timed out`, false, action.reason ? fill(action.reason, context.values()) : null);
  }
}

async function act (action, context) {
  logger.trace(`Tour action ${action.action}`, action.target ?? action.to ?? '', action.label ?? action.index ?? '');

  if (action.narration) {
    context.narrate(narration(action.narration, action.narrationValues, context.values()));
  }

  if (action.action === 'navigate') {
    const path = fill(action.to, context.values());
    const via = action.via ? await waitUntil(() => element({...action.via, action: 'click'}, context.values()), context.timing.optional, context) : null;

    if (via) {
      await press(via, context);
    }

    context.acting(true);
    context.navigate({to: path, resetSearchParams: true});
    const arrived = await waitUntil(() => window.location.pathname.replace(/\/$/, '') === path.replace(/\/$/, ''), context.timing.timeout, context);
    await pause(context.timing.action, context);
    context.acting(false);

    if (!arrived) {
      throw failure(`Navigating to ${path} did not arrive`);
    }
  } else if (['click', 'type'].includes(action.action)) {
    let el = action.row ? await findRow(action, context) :
      await waitUntil(() => element(action, context.values()), (action.optional || action.fallback) ? context.timing.optional : (action.timeout ?? context.timing.timeout), context, action.watch);

    if (!el && action.fallback) {
      for (const fallback of action.fallback) {
        await act(fallback, context);
      }
      el = await waitUntil(() => element(action, context.values()), action.timeout ?? context.timing.timeout, context, action.watch);
    }

    if (el) {
      await reveal(el, context);

      if (action.action === 'click') {
        context.acting(true);
        if (action.focus !== false) {
          await press(el, context);
        }
        el.click();
      } else {
        await typeInto(el, fill(action.value, context.values()), context);
      }

      await pause(context.timing.action, context);
      context.acting(false);
    } else if (!action.optional) {
      throw failure(`${action.label ?? action.index ?? ''} not found in ${action.target}`);
    }
  } else if (action.action === 'handover') {
    const el = await waitUntil(() => element(action, context.values()), action.timeout ?? context.timing.timeout, context, action.watch);

    if (el) {
      await reveal(el, context);
      context.handover(el);

      try {
        await waitUntil(() => holds(action.until, context.values(), context.marks), Infinity, context, action.watch);
      } finally {
        context.handover(null);
      }
    } else {
      throw failure(`${action.target} not found`);
    }
  } else if (action.action === 'set') {
    context.setData({[action.key]: action.value});
  } else if (action.action === 'stop') {
    throw failure(`Tour stopped at ${action.reason}`, false, fill(action.reason, context.values()));
  } else if (action.action === 'run') {
    context.run(action).then((result) => {
      if (action.into && utils.isEmpty(result)) {
        throw failure(`Tour task ${action.task} returned nothing`);
      }
      if (action.key) {
        context.setData({[action.key]: true});
      }
      if (action.into) {
        context.setData({[action.into]: result});
      }
    })
    .catch((error) => {
      logger.trace(`Tour task ${action.task} failed`, error);
      context.fail(error);
    });
  } else if (action.action === 'reveal') {
    const el = action.search ? await search(() => element(action, context.values()), () => {
      const scroller = dom.getScrollElement(document.querySelector(action.search), true, false);
      scroller?.scrollBy({left: Math.floor((scroller === window ? window.innerWidth : scroller.clientWidth) / 2), behavior: 'smooth'});
    }, context) : await waitUntil(() => element(action, context.values()), action.timeout ?? context.timing.timeout, context, action.watch);

    if (el) {
      await reveal(el, context, action.align);

      if (action.highlight) {
        context.spotlight(el);
        await pause(context.timing.read, context);
        context.spotlight(null);
      }
    } else if (!action.optional) {
      throw failure(`${action.target} not found`);
    }
  } else if (action.action === 'select') {
    await selectRows(action, context);
  } else if (action.action === 'waitFor') {
    const done = await waitUntil(() => {
      const found = Boolean(document.querySelector(action.target));
      return action.gone ? !found : found;
    }, action.timeout ?? context.timing.timeout, context, action.watch);

    if (!done) {
      throw failure(`Waiting for ${action.target} timed out`);
    }
  } else if (action.action === 'wait') {
    await pause(action.ms ?? context.timing.read, context);
  } else if (action.action === 'mark') {
    context.marks[action.key] = {
      size: utils.toArray(valueAt(sourceOf(action, context.values()), action.size)).length
    };
  } else if (action.action === 'until') {
    await until(action, context);
  } else if (action.action === 'resolve') {
    const match = await waitUntil(() => window.location.pathname.match(new RegExp(action.pattern)), action.timeout ?? context.timing.timeout, context);

    if (match?.[1]) {
      context.setData({[action.key]: match[1]});
    } else {
      throw failure(`${action.key} not found in ${window.location.pathname}`);
    }
  }
}

function stepApplies (step, values, marks = null) {
  return (!step.when || holds(step.when, values, marks)) && (!step.unless || !holds(step.unless, values, marks));
}

async function runStep (step, context) {
  let runs = 0;
  let again = true;

  while (again) {
    const roundContext = {...context, round: runs};

    for (const action of step.actions) {
      if (!action.when || holds(action.when, context.values(), context.marks)) {
        await act({...action, watch: action.watch ?? step.watch}, roundContext);
      }
    }

    runs += 1;
    again = Boolean(step.repeat) && runs < step.repeat.max && holds(step.repeat.while, context.values(), context.marks);
  }
}

function nextStep (definition, name, values, marks = null) {
  const index = definition.steps.findIndex((s) => {
    return s.name === name;
  });
  const next = definition.steps.slice(index + 1).find((s) => {
    return stepApplies(s, values, marks);
  });

  return next?.name ?? null;
}

function firstStep (definition, values) {
  const first = definition.steps.find((s) => {
    return stepApplies(s, values);
  });

  return first?.name ?? null;
}

const tour = {
  definition,
  narration,
  stepApplies,
  runStep,
  nextStep,
  firstStep
};

export default tour;
